// components/SegmentedImagesModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { showToast } from 'Core/redux/toast/slice';
import { MoldService } from '../../../../service/api/mold-service';
import { CellService } from '../../../../service/api/cell-service';
import { FaEye, FaEdit, FaSave } from 'react-icons/fa';
import '../../viewallimagesmodal/view-all-images-modal.css';
import ImagesListModal from '../imageslistmodal/images-list-modal';
import ViewAllImagesModal from '../../viewallimagesmodal/view-all-images-modal';

const SegmentedImagesModal = ({ show, onClose, documents = [], onDelete, moldInfoID, buttonLabel, maintenanceId = 0, context = 'maintenance', showLocalImages = false, savedMaintenanceActivityData, issueData }) => {
  const dispatch = useDispatch();
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [segments, setSegments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingSegments, setEditingSegments] = useState({});
  const [editingValues, setEditingValues] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [originalSegments, setOriginalSegments] = useState([]);
  const [segmentHasChanges, setSegmentHasChanges] = useState({});
  const [isSaving, setIsSaving] = useState({});
  const inputRefs = useRef({});

  // Fetch segments from API when modal opens
  useEffect(() => {
    // If showLocalImages is true, process local documents instead of calling API
    // But only if we don't need to edit metadata (which requires database IDs)
    if (showLocalImages && show && documents && documents.length > 0) {
      // Check if any document has a valid database ID
      const hasValidIds = documents.some(doc => doc.id && typeof doc.id === 'number' && doc.id > 0);
      
      if (hasValidIds) {
        // If we have valid IDs, process the documents
        processDocumentsToSegments(documents);
        return;
      } else {
        // If no valid IDs, fetch from database instead
        console.log('SegmentedImagesModal: No valid database IDs found in local documents, fetching from API instead');
        if (show && moldInfoID && buttonLabel) {
          fetchSegments();
        }
        return;
      }
    }
    
    // For actions context (buttonLabel is "Capture Image" or "Action Images"), show local images without API call
    // But only if showLocalImages is true (meaning we have local images to show)
    if (show && showLocalImages && (buttonLabel === "Capture Image" || buttonLabel === "Action Images")) {
      if (documents && documents.length > 0) {
        processDocumentsToSegments(documents);
      } else {
        setSegments([]);
        setIsLoading(false);
      }
      return;
    }
    
    // For receiving context, always call the API to get latest segments from database
    if (context === 'receiving') {
      if (show && moldInfoID && buttonLabel) {
        fetchSegments();
      } else {
        // For new records, show empty state
        if (show && buttonLabel && (moldInfoID === 0 || moldInfoID === null || moldInfoID === undefined)) {
          setSegments([]);
          setIsLoading(false);
        }
      }
      return;
    }
    
    // For machine, maintenance-records, and all asset management contexts, we need buttonLabel and maintenanceId, not moldInfoID
    if (context === 'machine' || context === 'maintenance-records' || context === 'auxiliary-equipment' || context === 'forklift' || context === 'water-towers' || context === 'air-compressors' || context === 'plant' || context === 'robots' || context === 'overhead-crane') {
      if (show && buttonLabel && maintenanceId) {
        fetchSegments();
      } else {
        // New / unsaved record: show parent-passed documents (e.g. from getMaintenanceRecordsDetails imageSegments) instead of empty
        if (show && buttonLabel && (maintenanceId === 0 || maintenanceId === null || maintenanceId === undefined)) {
          if (documents && documents.length > 0) {
            processDocumentsToSegments(documents);
          } else {
            setSegments([]);
            setIsLoading(false);
          }
        } else if (documents && documents.length > 0) {
          processDocumentsToSegments(documents);
        }
      }
    } else {
      // For other contexts (maintenance, release), use the original logic with moldInfoID
      if (show && moldInfoID && buttonLabel) {
        fetchSegments();
      } else {
        // For new records, we might want to show empty state instead of blocking the API call
        if (show && buttonLabel && (moldInfoID === 0 || moldInfoID === null || moldInfoID === undefined)) {
          setSegments([]);
          setIsLoading(false);
        } else {
          // Fallback to documents if API conditions not met
          if (documents && documents.length > 0) {
            processDocumentsToSegments(documents);
          }
        }
      }
    }
  }, [show, moldInfoID, buttonLabel, maintenanceId, context, documents]);

  // Focus input when editing starts
  useEffect(() => {
    Object.keys(editingSegments).forEach(segmentId => {
      if (editingSegments[segmentId] && inputRefs.current[segmentId]) {
        setTimeout(() => {
          inputRefs.current[segmentId]?.focus();
        }, 100);
      }
    });
  }, [editingSegments]);

  // Process documents from props to segments format
  const processDocumentsToSegments = (docs) => {
    console.log('SegmentedImagesModal: processDocumentsToSegments called with docs:', docs);
    setIsLoading(true);
    
    try {
      // Group by segment if segment property exists, otherwise group all under 'General'
      const groupedBySegment = docs.reduce((acc, item) => {
        const segment = item.slotName || item.segment || 'General';
        if (!acc[segment]) {
          acc[segment] = [];
        }
        
        const imageId = item.id || item.imageSegmentId || item.segmentId || null;
        console.log('SegmentedImagesModal: Processing image with ID:', imageId, 'for item:', item);
        
        acc[segment].push({
          id: imageId,
          name: item.fileName || item.name || `Image_${imageId || 'Unknown'}`,
          path: item.filePath || item.path || item.imageUrl,
          title: item.title || '',
          description: item.description || '',
          segment: item.slotName || item.segment || 'General',
          record: item.record || buttonLabel
        });
        return acc;
      }, {});
      
      setSegments(Object.entries(groupedBySegment).map(([segmentName, images], index) => ({
        id: `segment_${index}_${Date.now()}`, // Add unique ID
        segmentName,
        originalName: segmentName, // Store original name for comparison
        images
      })));
    } catch (error) {
      dispatch(showToast({ message: 'Failed to process documents', type: 'danger' }));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSegments = async () => {
    console.log('SegmentedImagesModal: fetchSegments called with savedMaintenanceActivityData:', savedMaintenanceActivityData);
    setIsLoading(true);
    try {
      let response;
      
      // If we have savedMaintenanceActivityData, use getMoldMaintenanceSegments with activity IDs
      if (savedMaintenanceActivityData && savedMaintenanceActivityData.actionImageId) {
        console.log('SegmentedImagesModal: Using activity IDs for API call');
        // For Add Actions (maintenance context with Action Images buttonLabel), use Sub Action Images
        const recordParam = (context === 'maintenance' && buttonLabel === 'Action Images') ? 'Sub Action Images' : (buttonLabel === 'Action Images' ? 'Action Images' : 'Sub Action Images');
        response = await MoldService.getMoldMaintenanceSegments(
          moldInfoID, 
          recordParam, // record parameter
          maintenanceId, // maintenanceId parameter
          savedMaintenanceActivityData.actionImageId, // actionImageId
          savedMaintenanceActivityData.subActionImageId // subActionImageId
        );
      } else if (context === 'receiving') {
        // Use the new Mold Receiving specific API
        response = await MoldService.getMoldReceivingSegments(moldInfoID, buttonLabel, 'Mold-Receiving');
      } else if (context === 'release') {
        // Use the Mold Receiving API for release context (same endpoint, different category)
        response = await MoldService.getMoldReceivingSegments(moldInfoID, buttonLabel, 'Mold-Release');
      } else if (context === 'machine') {
        // Use the Machine Maintenance specific API
        response = await CellService.getMachineMaintenanceImageSegments('Machine-Maintenance', buttonLabel, maintenanceId);
      } else if (context === 'auxiliary-equipment') {
        // Use the Auxiliary Equipment Maintenance specific API
        response = await CellService.getMachineMaintenanceImageSegments('Auxiliary-Equipment-Maintenance', buttonLabel, maintenanceId);
      } else if (context === 'forklift') {
        // Use the Forklift Maintenance specific API
        response = await CellService.getMachineMaintenanceImageSegments('Forklift-Maintenance', buttonLabel, maintenanceId);
      } else if (context === 'water-towers') {
        // Use the Water Towers Maintenance specific API
        response = await CellService.getMachineMaintenanceImageSegments('Water-Towers-Maintenance', buttonLabel, maintenanceId);
      } else if (context === 'air-compressors') {
        // Use the Air Compressors Maintenance specific API
        response = await CellService.getMachineMaintenanceImageSegments('Air-Compressors-Maintenance', buttonLabel, maintenanceId);
      } else if (context === 'plant') {
        // Use the Plant Maintenance specific API
        response = await CellService.getMachineMaintenanceImageSegments('Plant-Maintenance', buttonLabel, maintenanceId);
      } else if (context === 'robots') {
        // Use the Robots Maintenance specific API
        response = await CellService.getMachineMaintenanceImageSegments('Robots-Maintenance', buttonLabel, maintenanceId);
      } else if (context === 'overhead-crane') {
        // Use the Overhead Crane Maintenance specific API
        response = await CellService.getMachineMaintenanceImageSegments('Overhead-Crane-Maintenance', buttonLabel, maintenanceId);
      } else if (context === 'maintenance-records') {
        // Use the Machine Maintenance Records specific API
        response = await CellService.getMachineMaintenanceImageSegments('Machine-Maintenance-Records', buttonLabel, maintenanceId);
      } else if (context === 'other-maintenance-issue' && issueData) {
        // Handle special case for other-maintenance-issue context
        console.log('SegmentedImagesModal: Using other-maintenance-issue parameters');
        const recordParam = 'Action Images'; // Fixed as per requirement
        const actionImageId = issueData.activityIssueId || 0; // Use only activityIssueId or 0, not issueData.id
        const subActionImageId = 0; // Set to 0 as per requirement
        console.log('SegmentedImagesModal: other-maintenance-issue parameters:', {
          recordParam,
          actionImageId,
          subActionImageId,
          issueData: issueData,
          'issueData.activityIssueId': issueData.activityIssueId,
          'issueData.id': issueData.id,
          'Note': 'Using only activityIssueId from API response, 0 if new issue'
        });
        response = await MoldService.getMoldMaintenanceSegments(
          moldInfoID,
          recordParam,
          maintenanceId,
          actionImageId,
          subActionImageId
        );
      } else {
        // Use the existing maintenance API
        console.log('SegmentedImagesModal: Falling back to 3-parameter API call');
        console.log('savedMaintenanceActivityData:', savedMaintenanceActivityData);
        response = await MoldService.getMoldMaintenanceSegments(moldInfoID, buttonLabel, maintenanceId);
      }
      
      const list = Array.isArray(response?.response) ? response.response : [];
      if (list.length > 0) {
        // Group by segment (support camelCase + PascalCase from API)
        const groupedBySegment = list.reduce((acc, item) => {
          const segment = item.segment ?? item.Segment ?? 'Uncategorized';
          if (!acc[segment]) {
            acc[segment] = [];
          }
          const rowId = item.id ?? item.Id;
          const imageUrl = item.imageUrl ?? item.ImageUrl ?? '';
          acc[segment].push({
            id: rowId,
            name: `Image_${rowId}`,
            path: imageUrl,
            title: item.title ?? item.Title,
            description: item.description ?? item.Description,
            segment: item.segment ?? item.Segment,
            record: item.record ?? item.Record
          });
          return acc;
        }, {});

        const segmentsData = Object.entries(groupedBySegment).map(([segmentName, images], index) => ({
          id: `segment_${index}_${Date.now()}`, // Add unique ID
          segmentName,
          originalName: segmentName, // Store original name for comparison
          images
        }));

        setSegments(segmentsData);
        setOriginalSegments(segmentsData);
      } else if (documents && documents.length > 0) {
        processDocumentsToSegments(documents);
      } else {
        setSegments([]);
      }
    } catch (error) {
      dispatch(showToast({ message: 'Failed to fetch segments', type: 'danger' }));
      setSegments([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle segment name editing - Use segment ID instead of name
  const handleEditSegment = (segmentId, segmentName) => {
    setEditingSegments(prev => ({
      ...prev,
      [segmentId]: true
    }));
    setEditingValues(prev => ({
      ...prev,
      [segmentId]: segmentName
    }));
    setSegmentHasChanges(prev => ({
      ...prev,
      [segmentId]: false
    }));
  };

  // Handle canceling edit mode
  const handleCancelEdit = (segmentId) => {
    setEditingSegments(prev => ({ ...prev, [segmentId]: false }));
    setEditingValues(prev => ({ ...prev, [segmentId]: undefined }));
    setSegmentHasChanges(prev => ({ ...prev, [segmentId]: false }));
    setIsSaving(prev => ({ ...prev, [segmentId]: false }));
  };

  const handleSegmentNameChange = (segmentId, newName) => {
    console.log('handleSegmentNameChange called:', { segmentId, newName });
    
    // Find the segment to get its original name
    const segment = segments.find(seg => seg.id === segmentId);
    if (!segment) {
      console.error('Segment not found for ID:', segmentId);
      return;
    }
    
    const originalName = segment.originalName;
    
    setEditingValues(prev => ({
      ...prev,
      [segmentId]: newName
    }));
    
    // Check if this specific segment has changes
    const hasChange = newName !== originalName && newName.trim() !== '';
    console.log('Segment has changes:', hasChange);
    console.log('Setting segmentHasChanges for:', segmentId, 'to:', hasChange);
    
    setSegmentHasChanges(prev => {
      const newState = {
        ...prev,
        [segmentId]: hasChange
      };
      console.log('New segmentHasChanges state:', newState);
      return newState;
    });
    
    // Update the segments state immediately so the Save button logic works
    setSegments(prev => prev.map(segment => 
      segment.id === segmentId 
        ? { ...segment, segmentName: newName }
        : segment
    ));
  };

  const handleSegmentNameBlur = (segmentId) => {
    // Don't do anything on blur if we're currently saving this segment
    if (isSaving[segmentId]) {
      console.log('Blur ignored - currently saving segment:', segmentId);
      return;
    }
    
    const segment = segments.find(seg => seg.id === segmentId);
    if (!segment) return;
    
    const newName = editingValues[segmentId] !== undefined ? editingValues[segmentId] : segment.segmentName;
    
    // If the new name is empty, don't update (keep original name)
    if (newName.trim() === '') {
      setEditingSegments(prev => ({ ...prev, [segmentId]: false }));
      setSegmentHasChanges(prev => ({ ...prev, [segmentId]: false }));
      return;
    }
    
    if (newName !== segment.originalName) {
      setSegments(prev => {
        const updatedSegments = prev.map(seg => 
          seg.id === segmentId 
            ? { ...seg, segmentName: newName }
            : seg
        );
        
        // Check if there are changes using the updated segments
        const hasSegmentChanges = updatedSegments.some(seg => 
          seg.segmentName !== seg.originalName
        );
        setHasChanges(hasSegmentChanges);
        
        return updatedSegments;
      });
    }
    
    // Don't exit editing mode on blur - keep the input field and save button visible
    // Only exit editing mode when save is clicked or escape is pressed
  };

  const handleSaveIndividualSegment = async (segmentId) => {
    console.log('handleSaveIndividualSegment called with segmentId:', segmentId);
    
    const segment = segments.find(seg => seg.id === segmentId);
    if (!segment) {
      console.error('Segment not found for ID:', segmentId);
      return;
    }
    
    const segmentName = segment.segmentName;
    const originalName = segment.originalName;
    
    console.log('handleSaveIndividualSegment processing:', { segmentId, segmentName, originalName });
    
    try {
      setIsLoading(true);
      setIsSaving(prev => ({ ...prev, [segmentId]: true }));
      console.log('Starting save process...');
      
      // Get the API response data to extract the required fields
      const apiResponse = await fetchSegmentsForUpdate();
      console.log('API response received:', apiResponse);
      
      if (apiResponse && apiResponse.length > 0) {
        // Find the corresponding API data for this segment
        const apiData = apiResponse.find(item => item.segment === originalName);
        
        if (apiData) {
          // Prepare the update data with all required fields from the API response
          const updateData = {
            MoldInfoID: apiData.moldInfoID,
            MaintenanceId: apiData.maintenanceId,
            Category: apiData.category,
            Record: apiData.record,
            Segment: segmentName // Updated segment name
          };
          
          console.log('Updating individual segment with data:', updateData);
          console.log('Original API data for segment:', apiData);
          console.log('Original segment name:', originalName);
          
          // Call the updateMoldMaintenanceImageSegment API with both old and new segment names
          await MoldService.updateMoldMaintenanceImageSegment(updateData, originalName);
          
          dispatch(showToast({ 
            message: 'Segment updated successfully!', 
            type: 'success' 
          }));
          
          // Reset editing state for this segment
          setEditingSegments(prev => ({ ...prev, [segmentId]: false }));
          setEditingValues(prev => ({ ...prev, [segmentId]: undefined }));
          setSegmentHasChanges(prev => ({ ...prev, [segmentId]: false }));
          setIsSaving(prev => ({ ...prev, [segmentId]: false }));
          
          // Refresh the segments to get updated data
          await fetchSegments();
        } else {
          console.error('No API data found for segment:', originalName);
          console.log('Available segments in API response:', apiResponse.map(item => item.segment));
          dispatch(showToast({ 
            message: 'Segment data not found', 
            type: 'danger' 
          }));
        }
      } else {
        console.error('No API response data available');
        dispatch(showToast({ 
          message: 'No data available for update', 
          type: 'danger' 
        }));
      }
    } catch (error) {
      console.error('Error updating individual segment:', error);
      dispatch(showToast({ 
        message: 'Failed to update segment', 
        type: 'danger' 
      }));
    } finally {
      setIsLoading(false);
      setIsSaving(prev => ({ ...prev, [segmentId]: false }));
    }
  };

  // Fetch segments for update (reuse existing logic)
  const fetchSegmentsForUpdate = async () => {
    try {
      let response;
      
      // If we have savedMaintenanceActivityData, use getMoldMaintenanceSegments with activity IDs
      if (savedMaintenanceActivityData && savedMaintenanceActivityData.actionImageId) {
        console.log('fetchSegmentsForUpdate: Using activity IDs for API call');
        // For Add Actions (maintenance context with Action Images buttonLabel), use Sub Action Images
        const recordParam = (context === 'maintenance' && buttonLabel === 'Action Images') ? 'Sub Action Images' : (buttonLabel === 'Action Images' ? 'Action Images' : 'Sub Action Images');
        response = await MoldService.getMoldMaintenanceSegments(
          moldInfoID, 
          recordParam, // record parameter
          maintenanceId, // maintenanceId parameter
          savedMaintenanceActivityData.actionImageId, // actionImageId
          savedMaintenanceActivityData.subActionImageId // subActionImageId
        );
      } else if (context === 'receiving') {
        response = await MoldService.getMoldReceivingSegments(moldInfoID, buttonLabel, 'Mold-Receiving');
      } else if (context === 'release') {
        response = await MoldService.getMoldReceivingSegments(moldInfoID, buttonLabel, 'Mold-Release');
      } else if (context === 'machine') {
        response = await CellService.getMachineMaintenanceImageSegments('Machine-Maintenance', buttonLabel, maintenanceId);
      } else if (context === 'auxiliary-equipment') {
        response = await CellService.getMachineMaintenanceImageSegments('Auxiliary-Equipment-Maintenance', buttonLabel, maintenanceId);
      } else if (context === 'forklift') {
        response = await CellService.getMachineMaintenanceImageSegments('Forklift-Maintenance', buttonLabel, maintenanceId);
      } else if (context === 'water-towers') {
        response = await CellService.getMachineMaintenanceImageSegments('Water-Towers-Maintenance', buttonLabel, maintenanceId);
      } else if (context === 'air-compressors') {
        response = await CellService.getMachineMaintenanceImageSegments('Air-Compressors-Maintenance', buttonLabel, maintenanceId);
      } else if (context === 'plant') {
        response = await CellService.getMachineMaintenanceImageSegments('Plant-Maintenance', buttonLabel, maintenanceId);
      } else if (context === 'robots') {
        response = await CellService.getMachineMaintenanceImageSegments('Robots-Maintenance', buttonLabel, maintenanceId);
      } else if (context === 'overhead-crane') {
        response = await CellService.getMachineMaintenanceImageSegments('Overhead-Crane-Maintenance', buttonLabel, maintenanceId);
      } else if (context === 'maintenance-records') {
        response = await CellService.getMachineMaintenanceImageSegments('Machine-Maintenance-Records', buttonLabel, maintenanceId);
      } else if (context === 'other-maintenance-issue' && issueData) {
        // Handle special case for other-maintenance-issue context (same as fetchSegments)
        console.log('fetchSegmentsForUpdate: Using other-maintenance-issue parameters');
        const recordParam = 'Action Images'; // Fixed as per requirement
        const actionImageId = issueData.activityIssueId || 0; // Use only activityIssueId or 0
        const subActionImageId = 0; // Set to 0 as per requirement
        console.log('fetchSegmentsForUpdate: other-maintenance-issue parameters:', {
          recordParam,
          actionImageId,
          subActionImageId,
          issueData: issueData,
          'issueData.activityIssueId': issueData.activityIssueId
        });
        response = await MoldService.getMoldMaintenanceSegments(
          moldInfoID,
          recordParam,
          maintenanceId,
          actionImageId,
          subActionImageId
        );
      } else {
        // For maintenance context, use the same API call as when modal opens
        response = await MoldService.getMoldMaintenanceSegments(moldInfoID, buttonLabel, maintenanceId);
      }
      
      console.log('fetchSegmentsForUpdate - API response:', response);
      console.log('fetchSegmentsForUpdate - response.response:', response?.response);
      
      return response?.response || [];
    } catch (error) {
      console.error('Error fetching segments for update:', error);
      return [];
    }
  };

  return (
    <>
      <Modal show={show} onHide={onClose} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Manage Uploaded Images</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isLoading ? (
            <div className="text-center">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading segments...</p>
            </div>
          ) : segments.length === 0 ? (
            <div className="text-center">
              <p>No segments found for this record.</p>
            </div>
          ) : (
            <div className="row">
              {segments.map(({ id, segmentName, images, originalName }) => (
                <div
                  key={id}
                  className="col-12 mb-3"
                >
                  <div className="d-flex align-items-center justify-content-between p-3 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
                    <div className="d-flex align-items-center flex-grow-1">
                      {editingSegments[id] ? (
                        <input
                          ref={(el) => {
                            if (el) {
                              inputRefs.current[id] = el;
                            }
                          }}
                          type="text"
                          className="form-control me-2"
                          value={editingValues[id] !== undefined ? editingValues[id] : segmentName}
                          onChange={(e) => {
                            console.log('Input onChange triggered:', e.target.value);
                            handleSegmentNameChange(id, e.target.value);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              // Save the changes when Enter is pressed
                              console.log('Enter key pressed, segmentHasChanges:', segmentHasChanges[id]);
                              if (segmentHasChanges[id]) {
                                console.log('Calling handleSaveIndividualSegment from Enter key');
                                handleSaveIndividualSegment(id);
                              }
                            } else if (e.key === 'Escape') {
                              // Cancel editing when Escape is pressed
                              console.log('Escape key pressed, canceling edit');
                              handleCancelEdit(id);
                            }
                          }}
                          style={{ maxWidth: '200px' }}
                        />
                      ) : (
                        <span className="fw-semibold me-2" style={{ fontSize: '16px' }}>
                          {segmentName} ({images.length})
                        </span>
                      )}
                      
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => handleEditSegment(id, segmentName)}
                        title="Edit segment name"
                        style={{
                          borderColor: '#0d6efd',
                          color: '#0d6efd',
                          borderRadius: '6px',
                          padding: '6px 10px',
                          transition: 'all 0.2s ease',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#0d6efd';
                          e.target.style.color = 'white';
                          e.target.style.transform = 'translateY(-1px)';
                          e.target.style.boxShadow = '0 2px 6px rgba(0,0,0,0.15)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = '#0d6efd';
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                        }}
                      >
                        <FaEdit size={12} />
                      </button>
                    </div>
                    
                    <div className="d-flex gap-2">
                      {editingSegments[id] && (
                        <>
                          {segmentHasChanges[id] && (
                            <button
                              type="button"
                              className="btn btn-sm btn-success"
                              onMouseDown={(e) => {
                                e.preventDefault(); // Prevent input from losing focus
                                console.log('Save button mousedown for segment:', id);
                                handleSaveIndividualSegment(id);
                              }}
                              disabled={isLoading || isSaving[id]}
                              title="Save segment changes"
                              style={{
                                borderRadius: '6px',
                                padding: '6px 12px',
                                fontSize: '12px',
                                fontWeight: '500'
                              }}
                            >
                              <FaSave className="me-1" />
                              {isSaving[id] ? 'Saving...' : 'Save'}
                            </button>
                          )}
                          {/* Debug info */}
                          {console.log('Save button render check:', {
                            segmentId: id,
                            segmentName,
                            originalName,
                            segmentHasChanges: segmentHasChanges[id],
                            allSegmentHasChanges: segmentHasChanges,
                            editingSegments: editingSegments[id]
                          })}
                          
                          <button
                            type="button"
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleCancelEdit(id)}
                            title="Cancel editing"
                            style={{
                              borderRadius: '6px',
                              padding: '6px 12px',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}
                          >
                            Cancel
                          </button>
                        </>
                      )}
                      
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={() => setSelectedSegment({ slotName: segmentName, docs: images })}
                        title="View images"
                      >
                        <FaEye className="me-1" />
                        View Images
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-end w-100">
            <Button variant="warning" onClick={onClose}>
              Close
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {selectedSegment && (
        <ImagesListModal
        show={true}
          onClose={() => setSelectedSegment(null)}
          segmentName={selectedSegment.slotName}
         images={selectedSegment.docs}
         moldInfoID={moldInfoID}
         buttonLabel={buttonLabel}
         maintenanceId={maintenanceId}
         context={context}
         savedMaintenanceActivityData={savedMaintenanceActivityData}
         onImageSaved={async () => {
           // Refresh segments from API after image is saved
           console.log('🔄 Refreshing segments after image save...');
           try {
             let response;
             
             // Use the same logic as fetchSegments to get fresh data
             if (savedMaintenanceActivityData && savedMaintenanceActivityData.actionImageId) {
               const recordParam = (context === 'maintenance' && buttonLabel === 'Action Images') ? 'Sub Action Images' : (buttonLabel === 'Action Images' ? 'Action Images' : 'Sub Action Images');
               response = await MoldService.getMoldMaintenanceSegments(
                 moldInfoID, 
                 recordParam,
                 maintenanceId,
                 savedMaintenanceActivityData.actionImageId,
                 savedMaintenanceActivityData.subActionImageId
               );
             } else if (context === 'receiving') {
               response = await MoldService.getMoldReceivingSegments(moldInfoID, buttonLabel, 'Mold-Receiving');
             } else if (context === 'release') {
               response = await MoldService.getMoldReceivingSegments(moldInfoID, buttonLabel, 'Mold-Release');
             } else if (context === 'machine') {
               response = await CellService.getMachineMaintenanceImageSegments('Machine-Maintenance', buttonLabel, maintenanceId);
             } else {
               response = await MoldService.getMoldMaintenanceSegments(moldInfoID, buttonLabel, maintenanceId);
             }
             
             if (response?.response) {
               // Group by segment (same logic as fetchSegments)
               const groupedBySegment = response.response.reduce((acc, item) => {
                 const segment = item.segment || 'Uncategorized';
                 if (!acc[segment]) {
                   acc[segment] = [];
                 }
                 acc[segment].push({
                   id: item.id,
                   name: `Image_${item.id}`,
                   path: item.imageUrl,
                   title: item.title,
                   description: item.description,
                   segment: item.segment,
                   record: item.record
                 });
                 return acc;
               }, {});
               
               // Find the current segment and update selectedSegment with fresh data
               const currentSegmentName = selectedSegment.slotName;
               const freshDocs = groupedBySegment[currentSegmentName] || [];
               
               if (freshDocs.length > 0) {
                 console.log('✅ Refreshed images for segment:', currentSegmentName, 'Count:', freshDocs.length);
                 setSelectedSegment(prev => ({
                   ...prev,
                   docs: freshDocs
                 }));
                 
                 // Also update the segments state
                 const segmentsData = Object.entries(groupedBySegment).map(([segmentName, images], index) => ({
                   id: `segment_${index}_${Date.now()}`,
                   segmentName,
                   originalName: segmentName,
                   images
                 }));
                 setSegments(segmentsData);
               } else {
                 console.log('⚠️ No images found for segment:', currentSegmentName);
               }
             }
           } catch (error) {
             console.error('❌ Error refreshing segments after image save:', error);
             dispatch(showToast({ 
               message: 'Failed to refresh images. Please refresh manually.', 
               type: 'warning' 
             }));
           }
         }}
           onDelete={async (index) => {
             if (typeof onDelete === 'function') {
               const imageToDelete = selectedSegment.docs[index];
               console.log('SegmentedImagesModal: Deleting image:', imageToDelete);
               
               try {
                 await onDelete(selectedSegment.slotName, index, imageToDelete);
                 
                 // After successful delete, remove the specific row from the current segment
                 console.log('SegmentedImagesModal: Removing deleted row from UI');
                 const updatedDocs = selectedSegment.docs.filter((_, i) => i !== index);
                 setSelectedSegment(prev => ({
                   ...prev,
                   docs: updatedDocs
                 }));
                 
                 // Also update the segments state to reflect the change
                 setSegments(prevSegments => 
                   prevSegments.map(segment => 
                     segment.slotName === selectedSegment.slotName 
                       ? { ...segment, docs: updatedDocs }
                       : segment
                   )
                 );
               } catch (error) {
                 console.error('SegmentedImagesModal: Error during delete:', error);
               }
             }
           }}
        />
      )}
    </>
  );
};

export default SegmentedImagesModal;













// // components/SegmentedImagesModal.jsx
// import React, { useState, useEffect, useRef } from 'react';
// import { Modal, Button } from 'react-bootstrap';
// import { useDispatch } from 'react-redux';
// import { showToast } from 'Core/redux/toast/slice';
// import { MoldService } from '../../../../service/api/mold-service';
// import { CellService } from '../../../../service/api/cell-service';
// import { FaEye, FaEdit, FaSave } from 'react-icons/fa';
// import '../../viewallimagesmodal/view-all-images-modal.css';
// import ImagesListModal from '../imageslistmodal/images-list-modal';
// import ViewAllImagesModal from '../../viewallimagesmodal/view-all-images-modal';

// const SegmentedImagesModal = ({ show, onClose, documents = [], onDelete, moldInfoID, buttonLabel, maintenanceId = 0, context = 'maintenance' }) => {
//   const dispatch = useDispatch();
//   const [selectedSegment, setSelectedSegment] = useState(null);
//   const [segments, setSegments] = useState([]);
//   const [isLoading, setIsLoading] = useState(false);
//   const [editingSegments, setEditingSegments] = useState({});
//   const [editingValues, setEditingValues] = useState({});
//   const [hasChanges, setHasChanges] = useState(false);
//   const [originalSegments, setOriginalSegments] = useState([]);
//   const [segmentHasChanges, setSegmentHasChanges] = useState({});
//   const [isSaving, setIsSaving] = useState({});
//   const inputRefs = useRef({});

//   // Fetch segments from API when modal opens
//   useEffect(() => {
//     console.log('SegmentedImagesModal: useEffect triggered with:', { 
//       show, 
//       moldInfoID, 
//       buttonLabel, 
//       maintenanceId, 
//       context, 
//       documents,
//       showType: typeof show,
//       moldInfoIDType: typeof moldInfoID,
//       buttonLabelType: typeof buttonLabel,
//       contextType: typeof context
//     });
    
//     // For receiving context, always call the API to get latest segments from database
//     if (context === 'receiving') {
//       console.log('SegmentedImagesModal: Receiving context detected');
//       console.log('SegmentedImagesModal: Checking conditions - show:', show, 'moldInfoID:', moldInfoID, 'buttonLabel:', buttonLabel);
      
//       if (show && moldInfoID && buttonLabel) {
//         console.log('SegmentedImagesModal: Receiving context - All conditions met, calling fetchSegments');
//         fetchSegments();
//       } else {
//         console.log('SegmentedImagesModal: Receiving context - Conditions not met:', { 
//           show: !!show, 
//           moldInfoID: !!moldInfoID, 
//           buttonLabel: !!buttonLabel,
//           moldInfoIDValue: moldInfoID,
//           buttonLabelValue: buttonLabel,
//           context
//         });
        
//         // Show which specific condition is failing
//         if (!show) console.log('SegmentedImagesModal: show is false or undefined');
//         if (!moldInfoID) console.log('SegmentedImagesModal: moldInfoID is false, 0, null, or undefined');
//         if (!buttonLabel) console.log('SegmentedImagesModal: buttonLabel is false, empty, null, or undefined');
        
//         // For new records, show empty state
//         if (show && buttonLabel && (moldInfoID === 0 || moldInfoID === null || moldInfoID === undefined)) {
//           console.log('SegmentedImagesModal: New receiving record detected (moldInfoID = 0/null/undefined), setting empty segments');
//           setSegments([]);
//           setIsLoading(false);
//         }
//       }
//       return;
//     }
    
//     // Documents fallback logic moved to after API call logic to prevent bypassing API calls
    
//     // For machine and maintenance-records contexts, we need buttonLabel and maintenanceId, not moldInfoID
//     if (context === 'machine' || context === 'maintenance-records') {
//       if (show && buttonLabel && maintenanceId) {
//         console.log(`SegmentedImagesModal: ${context} context - All conditions met, calling fetchSegments`);
//         fetchSegments();
//       } else {
//         console.log(`SegmentedImagesModal: ${context} context - Conditions not met:`, { 
//           show: !!show, 
//           buttonLabel: !!buttonLabel,
//           maintenanceId: !!maintenanceId,
//           buttonLabelValue: buttonLabel,
//           maintenanceIdValue: maintenanceId,
//           context
//         });
//         // For new records, show empty state
//         if (show && buttonLabel && (maintenanceId === 0 || maintenanceId === null || maintenanceId === undefined)) {
//           console.log(`SegmentedImagesModal: New ${context} record detected (maintenanceId = 0/null/undefined), setting empty segments`);
//           setSegments([]);
//           setIsLoading(false);
//         } else {
//           // Fallback to documents if API conditions not met
//           if (documents && documents.length > 0) {
//             console.log('SegmentedImagesModal: Falling back to documents from props for machine/maintenance-records context');
//             processDocumentsToSegments(documents);
//           }
//         }
//       }
//     } else {
//       // For other contexts (maintenance, release), use the original logic with moldInfoID
//       if (show && moldInfoID && buttonLabel) {
//         console.log('SegmentedImagesModal: All conditions met, calling fetchSegments');
//         console.log('SegmentedImagesModal: Values - show:', show, 'moldInfoID:', moldInfoID, 'buttonLabel:', buttonLabel);
//         fetchSegments();
//       } else {
//         console.log('SegmentedImagesModal: Conditions not met:', { 
//           show: !!show, 
//           moldInfoID: !!moldInfoID, 
//           buttonLabel: !!buttonLabel,
//           showValue: show,
//           moldInfoIDValue: moldInfoID,
//           buttonLabelValue: buttonLabel,
//           context
//         });
//         console.log('SegmentedImagesModal: Detailed condition check:');
//         console.log('  - show is truthy:', !!show, 'value:', show);
//         console.log('  - moldInfoID is truthy:', !!moldInfoID, 'value:', moldInfoID);
//         console.log('  - buttonLabel is truthy:', !!buttonLabel, 'value:', buttonLabel);
//         // For new records, we might want to show empty state instead of blocking the API call
//         if (show && buttonLabel && (moldInfoID === 0 || moldInfoID === null || moldInfoID === undefined)) {
//           console.log('SegmentedImagesModal: New record detected (moldInfoID = 0/null/undefined), setting empty segments');
//           setSegments([]);
//           setIsLoading(false);
//         } else {
//           // Fallback to documents if API conditions not met
//           if (documents && documents.length > 0) {
//             console.log('SegmentedImagesModal: Falling back to documents from props for maintenance/release context');
//             processDocumentsToSegments(documents);
//           }
//         }
//       }
//     }
//   }, [show, moldInfoID, buttonLabel, maintenanceId, context, documents]);

//   // Focus input when editing starts
//   useEffect(() => {
//     Object.keys(editingSegments).forEach(segmentName => {
//       if (editingSegments[segmentName] && inputRefs.current[segmentName]) {
//         console.log('Focusing input for segment:', segmentName);
//         setTimeout(() => {
//           inputRefs.current[segmentName]?.focus();
//         }, 100);
//       }
//     });
//   }, [editingSegments]);

//   // Process documents from props to segments format
//   const processDocumentsToSegments = (docs) => {
//     console.log('SegmentedImagesModal: Processing documents to segments:', docs);
//     setIsLoading(true);
    
//     try {
//       // Group by segment if segment property exists, otherwise group all under 'General'
//       const groupedBySegment = docs.reduce((acc, item) => {
//         const segment = item.segment || 'General';
//         if (!acc[segment]) {
//           acc[segment] = [];
//         }
//         acc[segment].push({
//           id: item.id || Math.random(),
//           name: item.name || `Image_${item.id || Math.random()}`,
//           path: item.path || item.imageUrl,
//           title: item.title,
//           description: item.description,
//           segment: item.segment || 'General',
//           record: item.record
//         });
//         return acc;
//       }, {});
      
//       console.log('SegmentedImagesModal: Grouped documents:', groupedBySegment);
      
//       setSegments(Object.entries(groupedBySegment).map(([segmentName, images]) => ({
//         segmentName,
//         images
//       })));
//     } catch (error) {
//       console.error('Error processing documents to segments:', error);
//       dispatch(showToast({ message: 'Failed to process documents', type: 'danger' }));
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const fetchSegments = async () => {
//     console.log('SegmentedImagesModal: fetchSegments called with:', { moldInfoID, buttonLabel, maintenanceId, context });
//     console.log('SegmentedImagesModal: About to make API call with moldInfoID:', moldInfoID, 'buttonLabel:', buttonLabel);
//     setIsLoading(true);
//     try {
//       let response;
//       if (context === 'receiving') {
//         // Use the new Mold Receiving specific API
//         console.log('SegmentedImagesModal: Calling getMoldReceivingSegments with:', moldInfoID, buttonLabel, 'Mold-Receiving');
//         response = await MoldService.getMoldReceivingSegments(moldInfoID, buttonLabel, 'Mold-Receiving');
//         console.log('SegmentedImagesModal: Using getMoldReceivingSegments API');
//         console.log('SegmentedImagesModal: API response received:', response);
//       } else if (context === 'release') {
//         // Use the Mold Receiving API for release context (same endpoint, different category)
//         response = await MoldService.getMoldReceivingSegments(moldInfoID, buttonLabel, 'Mold-Release');
//         console.log('SegmentedImagesModal: Using getMoldReceivingSegments API for release context');
//       } else if (context === 'machine') {
//         // Use the Machine Maintenance specific API
//         response = await CellService.getMachineMaintenanceImageSegments('Machine-Maintenance', buttonLabel, maintenanceId);
//         console.log('SegmentedImagesModal: Using getMachineMaintenanceImageSegments API');
//       } else if (context === 'maintenance-records') {
//         // Use the Machine Maintenance Records specific API
//         response = await CellService.getMachineMaintenanceImageSegments('Machine-Maintenance-Records', buttonLabel, maintenanceId);
//         console.log('SegmentedImagesModal: Using getMachineMaintenanceImageSegments API for maintenance-records context');
//       } else {
//         // Use the existing maintenance API
//         response = await MoldService.getMoldMaintenanceSegments(moldInfoID, buttonLabel, maintenanceId);
//         console.log('SegmentedImagesModal: Using getMoldMaintenanceSegments API');
//       }
      
//       console.log('SegmentedImagesModal: API response:', response);
//       console.log('SegmentedImagesModal: response.response:', response?.response);
//       console.log('SegmentedImagesModal: response.response type:', typeof response?.response);
//       console.log('SegmentedImagesModal: response.response length:', response?.response?.length);
      
//       if (response?.response) {
//         // Group by segment
//         const groupedBySegment = response.response.reduce((acc, item) => {
//           const segment = item.segment || 'Uncategorized';
//           if (!acc[segment]) {
//             acc[segment] = [];
//           }
//           acc[segment].push({
//             id: item.id,
//             name: `Image_${item.id}`,
//             path: item.imageUrl,
//             title: item.title,
//             description: item.description,
//             segment: item.segment,
//             record: item.record
//           });
//           return acc;
//         }, {});
        
//         console.log('SegmentedImagesModal: Grouped segments:', groupedBySegment);
        
//         const segmentsData = Object.entries(groupedBySegment).map(([segmentName, images]) => ({
//           segmentName,
//           images,
//           originalName: segmentName // Store original name for comparison
//         }));
        
//         setSegments(segmentsData);
//         setOriginalSegments(segmentsData);
//       } else {
//         console.log('SegmentedImagesModal: No response.response found, setting empty segments');
//         setSegments([]);
//       }
//     } catch (error) {
//       console.error('Error fetching segments:', error);
//       console.error('Error details:', error.response || error.message);
//       dispatch(showToast({ message: 'Failed to fetch segments', type: 'danger' }));
//       setSegments([]);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   // Handle segment name editing
//   const handleEditSegment = (segmentName) => {
//     console.log('handleEditSegment called for:', segmentName);
//     setEditingSegments(prev => ({
//       ...prev,
//       [segmentName]: true
//     }));
//     setEditingValues(prev => ({
//       ...prev,
//       [segmentName]: segmentName
//     }));
//     setSegmentHasChanges(prev => ({
//       ...prev,
//       [segmentName]: false
//     }));
//   };

//   // Handle canceling edit mode
//   const handleCancelEdit = (originalName) => {
//     setEditingSegments(prev => ({ ...prev, [originalName]: false }));
//     setEditingValues(prev => ({ ...prev, [originalName]: undefined }));
//     setSegmentHasChanges(prev => ({ ...prev, [originalName]: false }));
//     setIsSaving(prev => ({ ...prev, [originalName]: false }));
//   };

//   const handleSegmentNameChange = (originalName, newName) => {
//     console.log('handleSegmentNameChange called:', { originalName, newName });
//     setEditingValues(prev => ({
//       ...prev,
//       [originalName]: newName
//     }));
    
//     // Check if this specific segment has changes
//     const hasChange = newName !== originalName && newName.trim() !== '';
//     console.log('Segment has changes:', hasChange);
//     console.log('Setting segmentHasChanges for:', originalName, 'to:', hasChange);
    
//     setSegmentHasChanges(prev => {
//       const newState = {
//         ...prev,
//         [originalName]: hasChange
//       };
//       console.log('New segmentHasChanges state:', newState);
//       return newState;
//     });
    
//     // Update the segments state immediately so the Save button logic works
//     if (hasChange) {
//       setSegments(prev => prev.map(segment => 
//         segment.segmentName === originalName 
//           ? { ...segment, segmentName: newName }
//           : segment
//       ));
//     }
//   };

//   const handleSegmentNameBlur = (oldName) => {
//     // Don't do anything on blur if we're currently saving this segment
//     if (isSaving[oldName]) {
//       console.log('Blur ignored - currently saving segment:', oldName);
//       return;
//     }
    
//     const newName = editingValues[oldName] !== undefined ? editingValues[oldName] : oldName;
    
//     // If the new name is empty, don't update (keep original name)
//     if (newName.trim() === '') {
//       setEditingSegments(prev => ({ ...prev, [oldName]: false }));
//       setSegmentHasChanges(prev => ({ ...prev, [oldName]: false }));
//       return;
//     }
    
//     if (newName !== oldName) {
//       setSegments(prev => {
//         const updatedSegments = prev.map(segment => 
//           segment.segmentName === oldName 
//             ? { ...segment, segmentName: newName }
//             : segment
//         );
        
//         // Check if there are changes using the updated segments
//         const hasSegmentChanges = updatedSegments.some(segment => 
//           segment.segmentName !== segment.originalName
//         );
//         setHasChanges(hasSegmentChanges);
        
//         return updatedSegments;
//       });
//     }
    
//     // Don't exit editing mode on blur - keep the input field and save button visible
//     // Only exit editing mode when save is clicked or escape is pressed
//   };

//   const handleSaveIndividualSegment = async (segmentName, originalName) => {
//     console.log('handleSaveIndividualSegment called with:', { segmentName, originalName });
//     try {
//       setIsLoading(true);
//       setIsSaving(prev => ({ ...prev, [originalName]: true }));
//       console.log('Starting save process...');
      
//       // Get the API response data to extract the required fields
//       const apiResponse = await fetchSegmentsForUpdate();
//       console.log('API response received:', apiResponse);
      
//       if (apiResponse && apiResponse.length > 0) {
//         // Find the corresponding API data for this segment
//         const apiData = apiResponse.find(item => item.segment === originalName);
        
//         if (apiData) {
//           // Prepare the update data with all required fields from the API response
//           const updateData = {
//             MoldInfoID: apiData.moldInfoID,
//             MaintenanceId: apiData.maintenanceId,
//             Category: apiData.category,
//             Record: apiData.record,
//             Segment: segmentName // Updated segment name
//           };
          
//           console.log('Updating individual segment with data:', updateData);
//           console.log('Original API data for segment:', apiData);
          
//           // Call the updateMoldMaintenanceImageSegment API
//           await MoldService.updateMoldMaintenanceImageSegment(updateData);
          
//           dispatch(showToast({ 
//             message: 'Segment updated successfully!', 
//             type: 'success' 
//           }));
          
//           // Reset editing state for this segment
//           setEditingSegments(prev => ({ ...prev, [originalName]: false }));
//           setEditingValues(prev => ({ ...prev, [originalName]: undefined }));
//           setSegmentHasChanges(prev => ({ ...prev, [originalName]: false }));
//           setIsSaving(prev => ({ ...prev, [originalName]: false }));
          
//           // Refresh the segments to get updated data
//           await fetchSegments();
//         } else {
//           console.error('No API data found for segment:', originalName);
//           console.log('Available segments in API response:', apiResponse.map(item => item.segment));
//           dispatch(showToast({ 
//             message: 'Segment data not found', 
//             type: 'danger' 
//           }));
//         }
//       } else {
//         console.error('No API response data available');
//         dispatch(showToast({ 
//           message: 'No data available for update', 
//           type: 'danger' 
//         }));
//       }
//     } catch (error) {
//       console.error('Error updating individual segment:', error);
//       dispatch(showToast({ 
//         message: 'Failed to update segment', 
//         type: 'danger' 
//       }));
//     } finally {
//       setIsLoading(false);
//       setIsSaving(prev => ({ ...prev, [originalName]: false }));
//     }
//   };


//   // Fetch segments for update (reuse existing logic)
//   const fetchSegmentsForUpdate = async () => {
//     try {
//       let response;
//       if (context === 'receiving') {
//         response = await MoldService.getMoldReceivingSegments(moldInfoID, buttonLabel, 'Mold-Receiving');
//       } else if (context === 'release') {
//         response = await MoldService.getMoldReceivingSegments(moldInfoID, buttonLabel, 'Mold-Release');
//       } else if (context === 'machine') {
//         response = await CellService.getMachineMaintenanceImageSegments('Machine-Maintenance', buttonLabel, maintenanceId);
//       } else if (context === 'maintenance-records') {
//         response = await CellService.getMachineMaintenanceImageSegments('Machine-Maintenance-Records', buttonLabel, maintenanceId);
//       } else {
//         // For maintenance context, use the same API call as when modal opens
//         response = await MoldService.getMoldMaintenanceSegments(moldInfoID, buttonLabel, maintenanceId);
//       }
      
//       console.log('fetchSegmentsForUpdate - API response:', response);
//       console.log('fetchSegmentsForUpdate - response.response:', response?.response);
      
//       return response?.response || [];
//     } catch (error) {
//       console.error('Error fetching segments for update:', error);
//       return [];
//     }
//   };

//   return (
//     <>
//       <Modal show={show} onHide={onClose} size="lg" centered>
//         <Modal.Header closeButton>
//           <Modal.Title>Manage Uploaded Images</Modal.Title>
//         </Modal.Header>
//         <Modal.Body>
//           {isLoading ? (
//             <div className="text-center">
//               <div className="spinner-border" role="status">
//                 <span className="visually-hidden">Loading...</span>
//               </div>
//               <p className="mt-2">Loading segments...</p>
//             </div>
//           ) : segments.length === 0 ? (
//             <div className="text-center">
//               <p>No segments found for this record.</p>
//             </div>
//           ) : (
//             // <div className="row">
//             //   {segments.map(({ segmentName, images }) => (
//             //     <div
//             //       key={segmentName}
//             //       className="col-lg-6 col-md-6 col-sm-12 mb-3 d-flex align-items-center"
//             //     >
//             //       <div
//             //         className="me-2 flex-grow-1 p-2 border rounded"
//             //         style={{
//             //           backgroundColor: '#fff3cd',
//             //           borderColor: '#ffc107',
//             //           color: '#856404',
//             //           fontWeight: '500',
//             //           fontSize: '14px',
//             //           minHeight: '38px',
//             //           display: 'flex',
//             //           alignItems: 'center',
//             //           justifyContent: 'center'
//             //         }}
//             //       >
//             //         {segmentName} ({images.length})
//             //       </div>
//             //       <Button
//             //         variant="dark"
//             //         onClick={() => setSelectedSegment({ slotName: segmentName, docs: images })}
//             //       >
//             //         <i className="feather-eye me-1" /> Manage Images
//             //       </Button>
//             //     </div>
//             //   ))}
//             // </div>
//             <div className="row">
//               {segments.map(({ segmentName, images, originalName }) => (
//                 <div
//                   key={segmentName}
//                   className="col-12 mb-3"
//                 >
//                   <div className="d-flex align-items-center justify-content-between p-3 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
//                     <div className="d-flex align-items-center flex-grow-1">
//                       {editingSegments[segmentName] ? (
//                         <input
//                           ref={(el) => {
//                             if (el) {
//                               inputRefs.current[segmentName] = el;
//                             }
//                           }}
//                           type="text"
//                           className="form-control me-2"
//                           value={editingValues[segmentName] !== undefined ? editingValues[segmentName] : segmentName}
//                           onChange={(e) => {
//                             console.log('Input onChange triggered:', e.target.value);
//                             handleSegmentNameChange(originalName, e.target.value);
//                           }}
//                           onKeyDown={(e) => {
//                             if (e.key === 'Enter') {
//                               // Save the changes when Enter is pressed
//                               console.log('Enter key pressed, segmentHasChanges:', segmentHasChanges[originalName]);
//                               if (segmentHasChanges[originalName]) {
//                                 console.log('Calling handleSaveIndividualSegment from Enter key');
//                                 handleSaveIndividualSegment(segmentName, originalName);
//                               }
//                             } else if (e.key === 'Escape') {
//                               // Cancel editing when Escape is pressed
//                               console.log('Escape key pressed, canceling edit');
//                               handleCancelEdit(originalName);
//                             }
//                           }}
//                           style={{ maxWidth: '200px' }}
//                         />
//                       ) : (
//                         <span className="fw-semibold me-2" style={{ fontSize: '16px' }}>
//                           {segmentName} ({images.length})
//                         </span>
//                       )}
                      
//                       <button
//                         type="button"
//                         className="btn btn-sm btn-outline-primary me-2"
//                         onClick={() => handleEditSegment(segmentName)}
//                         title="Edit segment name"
//                         style={{
//                           borderColor: '#0d6efd',
//                           color: '#0d6efd',
//                           borderRadius: '6px',
//                           padding: '6px 10px',
//                           transition: 'all 0.2s ease',
//                           boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
//                         }}
//                         onMouseEnter={(e) => {
//                           e.target.style.backgroundColor = '#0d6efd';
//                           e.target.style.color = 'white';
//                           e.target.style.transform = 'translateY(-1px)';
//                           e.target.style.boxShadow = '0 2px 6px rgba(0,0,0,0.15)';
//                         }}
//                         onMouseLeave={(e) => {
//                           e.target.style.backgroundColor = 'transparent';
//                           e.target.style.color = '#0d6efd';
//                           e.target.style.transform = 'translateY(0)';
//                           e.target.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
//                         }}
//                       >
//                         <FaEdit size={12} />
//                       </button>
//                     </div>
                    
//                     <div className="d-flex gap-2">
//                       {editingSegments[segmentName] && (
//                         <>
//                           {segmentHasChanges[originalName] && (
//                             <button
//                               type="button"
//                               className="btn btn-sm btn-success"
//                               onMouseDown={(e) => {
//                                 e.preventDefault(); // Prevent input from losing focus
//                                 console.log('Save button mousedown for segment:', segmentName, 'original:', originalName);
//                                 handleSaveIndividualSegment(segmentName, originalName);
//                               }}
//                               disabled={isLoading || isSaving[originalName]}
//                               title="Save segment changes"
//                               style={{
//                                 borderRadius: '6px',
//                                 padding: '6px 12px',
//                                 fontSize: '12px',
//                                 fontWeight: '500'
//                               }}
//                             >
//                               <FaSave className="me-1" />
//                               {isSaving[originalName] ? 'Saving...' : 'Save'}
//                             </button>
//                           )}
//                           {/* Debug info */}
//                           {console.log('Save button render check:', {
//                             segmentName,
//                             originalName,
//                             segmentHasChanges: segmentHasChanges[originalName],
//                             allSegmentHasChanges: segmentHasChanges,
//                             editingSegments: editingSegments[segmentName]
//                           })}
                          
//                           <button
//                             type="button"
//                             className="btn btn-sm btn-secondary"
//                             onClick={() => handleCancelEdit(originalName)}
//                             title="Cancel editing"
//                             style={{
//                               borderRadius: '6px',
//                               padding: '6px 12px',
//                               fontSize: '12px',
//                               fontWeight: '500'
//                             }}
//                           >
//                             Cancel
//                           </button>
//                         </>
//                       )}
                      
//                       <button
//                         type="button"
//                         className="btn btn-sm btn-primary"
//                         onClick={() => setSelectedSegment({ slotName: segmentName, docs: images })}
//                         title="View images"
//                       >
//                         <FaEye className="me-1" />
//                         View Images
//                       </button>
//                     </div>
//                   </div>
//                 </div>
//               ))}
//             </div>

//           )}
//         </Modal.Body>
//         <Modal.Footer>
//           <div className="d-flex justify-content-end w-100">
//             <Button variant="warning" onClick={onClose}>
//               Close
//             </Button>
//           </div>
//         </Modal.Footer>
//       </Modal>

//       {selectedSegment && (
//         <ImagesListModal
//         show={true}
//           onClose={() => setSelectedSegment(null)}
//           segmentName={selectedSegment.slotName}
//          images={selectedSegment.docs}
//            onDelete={async (index) => {
//             if (typeof onDelete === 'function') {
//               const imageToDelete = selectedSegment.docs[index];
//               console.log('SegmentedImagesModal: Deleting image:', imageToDelete);
              
//               try {
//                 await onDelete(selectedSegment.slotName, index, imageToDelete);
                
//                 // After successful delete, remove the specific row from the current segment
//                 console.log('SegmentedImagesModal: Removing deleted row from UI');
//                 const updatedDocs = selectedSegment.docs.filter((_, i) => i !== index);
//                 setSelectedSegment(prev => ({
//                   ...prev,
//                   docs: updatedDocs
//                 }));
                
//                 // Also update the segments state to reflect the change
//                 setSegments(prevSegments => 
//                   prevSegments.map(segment => 
//                     segment.slotName === selectedSegment.slotName 
//                       ? { ...segment, docs: updatedDocs }
//                       : segment
//                   )
//                 );
//               } catch (error) {
//                 console.error('SegmentedImagesModal: Error during delete:', error);
//               }
//             }
//           }}
//         />
//       )}
//     </>
//   );
// };

// export default SegmentedImagesModal;
